/**
 * Theme Service
 * 
 * Handles theme persistence and application to the DOM.
 */

import { getThemeById, getDefaultTheme } from './themes'
import type { Theme, ThemeColors, ThemeId } from './types'

const STORAGE_KEY = 'appic-theme-preference'

/**
 * Load theme preference from localStorage
 */
export function loadThemePreference(): ThemeId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const themeId = JSON.parse(stored) as ThemeId
      if (getThemeById(themeId)) {
        return themeId
      }
    }
  } catch (error) {
    console.warn('Failed to load theme preference:', error)
  }
  return 'default'
}

/**
 * Save theme preference to localStorage
 */
export function saveThemePreference(themeId: ThemeId): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(themeId))
  } catch (error) {
    console.warn('Failed to save theme preference:', error)
  }
}

/**
 * Apply theme colors to CSS variables
 * Maps theme colors to existing CSS variable names
 */
export function applyThemeColors(colors: ThemeColors): void {
  const root = document.documentElement
  
  // Map theme colors to CSS variables — only theme-specific overrides (CSS handles baseline)
  const variableMap: Record<string, string> = {
    accent: '--color-accent',
    accentForeground: '--color-accent-foreground',
    accentSoft: '--color-accent-soft',
    accentSoftForeground: '--accent-foreground',
    ring: '--ring',
    chart1: '--chart-1',
    sidebarAccent: '--sidebar-accent',
    sidebarAccentForeground: '--sidebar-accent-fg',
    sidebarAccentSoft: '--sidebar-accent-soft',
    sidebarAccentSoftForeground: '--sidebar-accent-soft-fg',
    sidebarRing: '--sidebar-ring',
  }
  
  // Apply all color variables
  Object.entries(colors).forEach(([key, value]) => {
    if (value && variableMap[key as keyof ThemeColors]) {
      const cssVar = variableMap[key as keyof ThemeColors]
      root.style.setProperty(cssVar, value)
    }
  })
}

/**
 * Apply theme to the document
 */
export function applyTheme(theme: Theme, isDark: boolean = false): void {
  const colors = isDark ? theme.dark : theme.light
  applyThemeColors(colors)
  
  // Set data attribute for theme-specific styling
  document.documentElement.setAttribute('data-theme', theme.id)
}

/**
 * Initialize theme on app load
 */
export function initializeTheme(isDark: boolean = false): Theme {
  const themeId = loadThemePreference()
  const theme = getThemeById(themeId) || getDefaultTheme()
  applyTheme(theme, isDark)
  return theme
}

/**
 * Switch theme
 */
export function switchTheme(themeId: ThemeId, isDark: boolean = false): Theme {
  const theme = getThemeById(themeId) || getDefaultTheme()
  applyTheme(theme, isDark)
  saveThemePreference(theme.id as ThemeId)
  return theme
}
